import { createReducer, on } from '@ngrx/store';
import { RequestStatus } from '../../enums/request-status.enum';
import * as ProfileActions from './profile.actions';
import {
  UserProfile,
  Department,
  Timezone,
} from '../models/user-profile.model';

export type ProfileData = UserProfile & {
  departments: Department[];
  timezones: Timezone[];
};

export interface ProfileState {
  data: ProfileData | null;
  status: RequestStatus;
  updateStatus: RequestStatus;
  departmentsStatus: RequestStatus;
  timezonesStatus: RequestStatus;
  regenerateTokenStatus: RequestStatus;
  uploadPhotoStatus: RequestStatus;
  error: string | null;
}

const initialState: ProfileState = {
  data: null,
  status: RequestStatus.Idle,
  updateStatus: RequestStatus.Idle,
  departmentsStatus: RequestStatus.Idle,
  timezonesStatus: RequestStatus.Idle,
  regenerateTokenStatus: RequestStatus.Idle,
  uploadPhotoStatus: RequestStatus.Idle,
  error: null,
};

export const profileReducer = createReducer(
  initialState,

  on(ProfileActions.loadProfile, (state) => ({
    ...state,
    status: RequestStatus.Loading,
    error: null,
  })),

  on(ProfileActions.loadProfileSuccess, (state, { profile }) => ({
    ...state,
    data: state.data
      ? { ...state.data, ...profile, departments: state.data.departments, timezones: state.data.timezones }
      : { ...profile, departments: [], timezones: [] },
    status: RequestStatus.Success,
    error: null,
  })),

  on(ProfileActions.loadProfileFailure, (state, { error }) => ({
    ...state,
    status: RequestStatus.Error,
    error,
  })),

  on(ProfileActions.updateProfile, (state) => ({
    ...state,
    updateStatus: RequestStatus.Loading,
    error: null,
  })),

  on(ProfileActions.updateProfileSuccess, (state, { profile }) => ({
    ...state,
    data: state.data
      ? { ...state.data, ...profile, departments: state.data.departments, timezones: state.data.timezones }
      : { ...profile, departments: [], timezones: [] },
    updateStatus: RequestStatus.Success,
    error: null,
  })),

  on(ProfileActions.updateProfileFailure, (state, { error }) => ({
    ...state,
    updateStatus: RequestStatus.Error,
    error,
  })),

  on(ProfileActions.loadDepartments, (state) => ({
    ...state,
    departmentsStatus: RequestStatus.Loading,
  })),

  on(ProfileActions.loadDepartmentsSuccess, (state, { departments }) => ({
    ...state,
    data: state.data
      ? { ...state.data, departments }
      : { departments, timezones: [] } as ProfileData,
    departmentsStatus: RequestStatus.Success,
  })),

  on(ProfileActions.loadDepartmentsFailure, (state, { error }) => ({
    ...state,
    departmentsStatus: RequestStatus.Error,
    error,
  })),

  on(ProfileActions.loadTimezones, (state) => ({
    ...state,
    timezonesStatus: RequestStatus.Loading,
  })),

  on(ProfileActions.loadTimezonesSuccess, (state, { timezones }) => ({
    ...state,
    data: state.data
      ? { ...state.data, timezones }
      : { departments: [], timezones } as ProfileData,
    timezonesStatus: RequestStatus.Success,
  })),

  on(ProfileActions.loadTimezonesFailure, (state, { error }) => ({
    ...state,
    timezonesStatus: RequestStatus.Error,
    error,
  })),

  on(ProfileActions.regenerateToken, (state) => ({
    ...state,
    regenerateTokenStatus: RequestStatus.Loading,
    error: null,
  })),

  on(ProfileActions.regenerateTokenSuccess, (state, { refreshToken }) => ({
    ...state,
    data: state.data
      ? { ...state.data, refreshToken, departments: state.data.departments, timezones: state.data.timezones }
      : { refreshToken, departments: [], timezones: [] } as ProfileData,
    regenerateTokenStatus: RequestStatus.Success,
    error: null,
  })),

  on(ProfileActions.regenerateTokenFailure, (state, { error }) => ({
    ...state,
    regenerateTokenStatus: RequestStatus.Error,
    error,
  })),

  on(ProfileActions.uploadPhoto, (state) => ({
    ...state,
    uploadPhotoStatus: RequestStatus.Loading,
    error: null,
  })),

  on(ProfileActions.uploadPhotoSuccess, (state, { photo }) => ({
    ...state,
    data: state.data
      ? { ...state.data, photo, departments: state.data.departments, timezones: state.data.timezones }
      : { photo, departments: [], timezones: [] } as ProfileData,
    uploadPhotoStatus: RequestStatus.Success,
    error: null,
  })),

  on(ProfileActions.uploadPhotoFailure, (state, { error }) => ({
    ...state,
    uploadPhotoStatus: RequestStatus.Error,
    error,
  })),

  on(ProfileActions.deletePhoto, (state) => ({
    ...state,
    uploadPhotoStatus: RequestStatus.Loading,
    error: null,
  })),

  on(ProfileActions.deletePhotoSuccess, (state) => ({
    ...state,
    data: state.data
      ? { ...state.data, photo: null, departments: state.data.departments, timezones: state.data.timezones }
      : { photo: null, departments: [], timezones: [] } as ProfileData,
    uploadPhotoStatus: RequestStatus.Success,
    error: null,
  })),

  on(ProfileActions.deletePhotoFailure, (state, { error }) => ({
    ...state,
    uploadPhotoStatus: RequestStatus.Error,
    error,
  })),

  on(ProfileActions.resetUpdateStatus, (state) => ({
    ...state,
    updateStatus: RequestStatus.Idle,
    error: null,
  }))
);
